/**
 * VoicePresenceDetector
 *
 * Uses Web Audio API to classify who is present at the device
 * based on voice pitch (fundamental frequency / F0).
 *
 * This is NOT speech recognition. No words are captured.
 * No audio is recorded or stored. Only pitch frequency is analyzed.
 *
 * Vocal pitch ranges:
 *   Toddler (1-3):   ~250–400 Hz
 *   Child (3-7):      ~220–350 Hz
 *   Adult female:     ~165–255 Hz
 *   Adult male:       ~85–180 Hz
 *
 * Classification boundary: 240 Hz
 *   F0 > 240 Hz → pupil (child)
 *   F0 < 240 Hz → mentor (adult)
 *
 * Algorithm: autocorrelation-based pitch detection (simplified YIN)
 * on the Web Audio AnalyserNode. Runs at ~4 Hz (every 250ms).
 *
 * Privacy guarantees:
 *   - No audio buffers leave this module
 *   - No audio is stored, transmitted, or transcribed
 *   - Only { voice_active, pupil_detected, mentor_detected, pitch_hz } is emitted
 *   - Microphone stream is never connected to MediaRecorder
 *   - On stop(), the stream tracks are killed immediately
 */

import type { RoomPresence } from '@aegis/feature-stream';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

export interface VoicePresenceConfig {
  /** Pitch boundary between child and adult (Hz). Default: 240 */
  child_adult_boundary_hz: number;
  /** RMS amplitude threshold for voice activity. Default: 0.01 */
  vad_threshold: number;
  /** Detection interval (ms). Default: 250 (4 Hz) */
  interval_ms: number;
  /** EWMA smoothing factor for pitch. Default: 0.3 */
  pitch_alpha: number;
  /** Minimum voiced frames before classification. Default: 3 */
  min_voiced_frames: number;
}

const DEFAULT_CONFIG: VoicePresenceConfig = {
  child_adult_boundary_hz: 240,
  vad_threshold: 0.01,
  interval_ms: 250,
  pitch_alpha: 0.3,
  min_voiced_frames: 3,
};

// ---------------------------------------------------------------------------
// Detector
// ---------------------------------------------------------------------------

export type VoicePresenceCallback = (presence: RoomPresence) => void;

export class VoicePresenceDetector {
  private config: VoicePresenceConfig;
  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private stream: MediaStream | null = null;
  private timeDomainBuffer: Float32Array | null = null;
  private intervalId: number | null = null;
  private callback: VoicePresenceCallback;

  // Smoothed state
  private smoothedPitch = 0;
  private voicedFrameCount = 0;
  private pupilFrames = 0;
  private mentorFrames = 0;
  private recentWindow = 20; // frames (~5 seconds at 4Hz)

  // Rolling history for multi-speaker detection
  private pitchHistory: number[] = [];

  constructor(callback: VoicePresenceCallback, config?: Partial<VoicePresenceConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.callback = callback;
  }

  /**
   * Request microphone permission and start detection.
   * Returns true if microphone was granted.
   */
  async start(): Promise<boolean> {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });

      this.audioCtx = new AudioContext();
      this.source = this.audioCtx.createMediaStreamSource(this.stream);

      // AnalyserNode: 2048-sample FFT for good low-frequency resolution
      this.analyser = this.audioCtx.createAnalyser();
      this.analyser.fftSize = 2048;
      this.analyser.smoothingTimeConstant = 0.5;

      // Connect source → analyser (NOT to destination — no playback)
      this.source.connect(this.analyser);

      this.timeDomainBuffer = new Float32Array(this.analyser.fftSize);

      // Start detection loop
      this.intervalId = window.setInterval(
        () => this.detect(),
        this.config.interval_ms,
      );

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Stop detection and release microphone.
   */
  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    // Kill all audio tracks immediately
    if (this.stream) {
      this.stream.getTracks().forEach(t => t.stop());
      this.stream = null;
    }

    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }

    if (this.audioCtx) {
      this.audioCtx.close();
      this.audioCtx = null;
    }

    this.analyser = null;
    this.timeDomainBuffer = null;
    this.pitchHistory = [];
    this.voicedFrameCount = 0;
    this.pupilFrames = 0;
    this.mentorFrames = 0;
  }

  /**
   * Check if the detector is currently running.
   */
  isRunning(): boolean {
    return this.intervalId !== null;
  }

  // -------------------------------------------------------------------------
  // Private: detection loop
  // -------------------------------------------------------------------------

  private detect(): void {
    if (!this.analyser || !this.timeDomainBuffer) return;

    // Get time-domain data
    this.analyser.getFloatTimeDomainData(this.timeDomainBuffer);

    // Step 1: Voice activity detection (RMS energy)
    const rms = this.computeRMS(this.timeDomainBuffer);
    const voiceActive = rms > this.config.vad_threshold;

    if (!voiceActive) {
      // No voice — emit silent presence
      this.voicedFrameCount = Math.max(0, this.voicedFrameCount - 1);
      this.emitPresence(false, 0);
      return;
    }

    // Step 2: Pitch detection via autocorrelation
    const pitch = this.detectPitch(this.timeDomainBuffer, this.audioCtx!.sampleRate);

    if (pitch === 0) {
      // Voiced but pitch undetectable (noise, multiple speakers, etc.)
      this.emitPresence(true, 0);
      return;
    }

    // Step 3: Smooth and classify
    if (this.smoothedPitch === 0) {
      this.smoothedPitch = pitch;
    } else {
      this.smoothedPitch = this.config.pitch_alpha * pitch
        + (1 - this.config.pitch_alpha) * this.smoothedPitch;
    }

    this.voicedFrameCount++;

    // Track pitch history for multi-speaker detection
    this.pitchHistory.push(this.smoothedPitch);
    if (this.pitchHistory.length > this.recentWindow) {
      this.pitchHistory.shift();
    }

    // Classify current frame
    if (this.smoothedPitch > this.config.child_adult_boundary_hz) {
      this.pupilFrames = Math.min(this.pupilFrames + 1, this.recentWindow);
      this.mentorFrames = Math.max(this.mentorFrames - 0.5, 0);
    } else {
      this.mentorFrames = Math.min(this.mentorFrames + 1, this.recentWindow);
      this.pupilFrames = Math.max(this.pupilFrames - 0.5, 0);
    }

    this.emitPresence(true, this.smoothedPitch);
  }

  private emitPresence(voiceActive: boolean, pitch: number): void {
    const minFrames = this.config.min_voiced_frames;

    const presence: RoomPresence = {
      voice_active: voiceActive,
      pupil_detected: this.pupilFrames >= minFrames,
      mentor_detected: this.mentorFrames >= minFrames,
      dominant_pitch_hz: Math.round(pitch),
      confidence: this.computeConfidence(),
    };

    this.callback(presence);
  }

  // -------------------------------------------------------------------------
  // Private: pitch detection (autocorrelation / simplified YIN)
  // -------------------------------------------------------------------------

  /**
   * Autocorrelation-based pitch detection.
   *
   * We look for the first dip-then-rise in the autocorrelation function,
   * which corresponds to the fundamental period.
   *
   * Search range: 80 Hz to 500 Hz (covers adult male to toddler).
   */
  private detectPitch(buffer: Float32Array, sampleRate: number): number {
    const size = buffer.length;

    // Min/max lag corresponding to frequency range
    const minLag = Math.floor(sampleRate / 500); // 500 Hz max
    const maxLag = Math.ceil(sampleRate / 80);   // 80 Hz min

    // Compute normalized autocorrelation
    let bestCorrelation = 0;
    let bestLag = 0;
    let foundDip = false;

    for (let lag = minLag; lag <= maxLag && lag < size; lag++) {
      let correlation = 0;
      let norm1 = 0;
      let norm2 = 0;

      const len = size - lag;
      for (let i = 0; i < len; i++) {
        correlation += buffer[i] * buffer[i + lag];
        norm1 += buffer[i] * buffer[i];
        norm2 += buffer[i + lag] * buffer[i + lag];
      }

      const normFactor = Math.sqrt(norm1 * norm2);
      if (normFactor === 0) continue;

      const r = correlation / normFactor;

      // Look for a dip first (ensures we skip the initial peak at lag=0 region)
      if (!foundDip && r < 0.5) {
        foundDip = true;
      }

      // After the dip, find the best peak
      if (foundDip && r > bestCorrelation) {
        bestCorrelation = r;
        bestLag = lag;
      }
    }

    // Require minimum correlation quality
    if (bestCorrelation < 0.6 || bestLag === 0) {
      return 0; // No confident pitch
    }

    return sampleRate / bestLag;
  }

  // -------------------------------------------------------------------------
  // Private: helpers
  // -------------------------------------------------------------------------

  private computeRMS(buffer: Float32Array): number {
    let sum = 0;
    for (let i = 0; i < buffer.length; i++) {
      sum += buffer[i] * buffer[i];
    }
    return Math.sqrt(sum / buffer.length);
  }

  private computeConfidence(): number {
    if (this.voicedFrameCount < this.config.min_voiced_frames) {
      return 0.1;
    }

    // Higher confidence with more consistent pitch data
    const pitchVariance = this.computePitchVariance();
    const consistencyBonus = pitchVariance < 30 ? 0.3 : 0; // Low variance = consistent
    const frameBonus = Math.min(this.voicedFrameCount / 10, 0.3);

    return Math.min(0.3 + consistencyBonus + frameBonus, 0.95);
  }

  private computePitchVariance(): number {
    if (this.pitchHistory.length < 2) return 999;

    const mean = this.pitchHistory.reduce((a, b) => a + b, 0) / this.pitchHistory.length;
    const variance = this.pitchHistory.reduce((sum, p) => sum + (p - mean) ** 2, 0)
      / this.pitchHistory.length;
    return Math.sqrt(variance);
  }
}
